import { useUnitsFormatter } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { useTenant } from "hooks/settings";
import { Loaded, loadedEndpoint, mapLoaded } from "models/loaded";
import { useMemo } from "react";
import {
  CrewAggregatedComplianceData,
  StrategyDashboardComplianceData,
  UnitType,
  useGetStrategyDashboardComplianceQuery,
  useGetCrewAggregatedComplianceQuery,
} from "src/store/api/generatedApi";
import { CompliancePodiumScores, deriveCompliancePodiumScores } from "./utils";

export const useStrategyDashboardCompliance = (
  start: string,
  end: string
): Loaded<StrategyDashboardComplianceData> => {
  const loadedRawData = loadedEndpoint(
    useGetStrategyDashboardComplianceQuery({
      tenantName: useTenant(),
      start,
      end,
    })
  );
  if (loadedRawData.status === "success") {
    return {
      status: "success",
      data: loadedRawData.data,
      fetching: loadedRawData.fetching,
    };
  }
  return loadedRawData;
};

export const useCrewAggregatedCompliance = (
  start: string,
  end: string,
  excludeMaterials: string[]
): Loaded<CrewAggregatedComplianceData> => {
  const loadedRawData = loadedEndpoint(
    useGetCrewAggregatedComplianceQuery({
      tenantName: useTenant(),
      start,
      end,
      excludeMaterials,
    })
  );
  if (loadedRawData.status === "success") {
    return {
      status: "success",
      data: loadedRawData.data,
      fetching: loadedRawData.fetching,
    };
  }
  return loadedRawData;
};

export const useCompliancePodiumScores = (
  start: string,
  end: string
): Loaded<CompliancePodiumScores> =>
  mapLoaded(
    useStrategyDashboardCompliance(start, end),
    deriveCompliancePodiumScores
  );

export const useComplianceUnitsFormatter = () => {
  const { format } = useNumberSerialiser({ decimalPlaces: 1 });
  const units = useUnitsFormatter(false);

  return useMemo(() => {
    return (value: number, unit: UnitType): string => {
      switch (unit) {
        case "mass":
          return `${format(value, { decimalPlaces: 0 })}${units("mass")}`;
        case "mass_fraction":
          return `${format(value * 100, { decimalPlaces: 1 })}${units(
            "mass_fraction"
          )}`;
        default:
          return "Unimplemented Unit";
      }
    };
  }, [units, format]);
};
